import Events from '@/configuration/Events'
import DebugBarTestCards from '@/toolbar/configuration/DebugBarTestCards.yml'
import getAllowedBrandsFromNetworks from '@/toolbar/util/getAllowedBrandsFromNetworks.js'
import { initInspector } from '@/toolbar/inspector/initInspector'
import { intersection, sortBy, isArray, contains, indexOf } from 'underscore'

export const getToolbarState = () => {
  return {
    toolbar: {
      reload: false,
      showLegacy: false
    }
  }
}

export const toolbarActions = $locator => {
  return {
    async loadToolbar({ state, commit }) {
      // If it's chrome40 or minor, doesn't show the toolbar
      if (
        state.browser?.name === 'Chrome' &&
        parseFloat(state.browser?.version) <= 40
      )
        return
      // The toolbar is only created on the host
      const isHost = state.application === 'host'
      // Only create the toolbar if has not already been created and the public key is test
      if (
        isHost &&
        state.dna &&
        state.testKeys &&
        global.KR?.toolbarReady === false
      ) {
        KR.toolbarReady = true

        //invoketoolbar
        if (state.hiddenToolbar) return false

        const lazyLoader = $locator.lazyLoader
        const $bus = $locator.$bus
        const timerMan = $locator.timerManager

        $bus.$on(Events.krypton.destroy, message => {
          // Clean all events
          KR.toolbarReady = false
          commit('UPDATE', { toolbar: { reload: true } })
        })

        timerMan.register('start', 'creatingToolbar', 'Creating toolbar', true)

        if (!state.toolbar.reload) {
          const toolbarCSS = `${state.baseAddress}/checkout/css/toolbar.min.css?assets=%%build_number%%`
          await lazyLoader.load(toolbarCSS, 'css')
          const animateCSS = `${state.baseAddress}/vendors_static/css/animate.min.css?assets=%%build_number%%`
          await lazyLoader.load(animateCSS, 'css')
        }

        const module = await import(
          /* webpackChunkName: "kr-toolbar" */ '@/toolbar/Loader'
        )
        const loadToolbar = module.default
        loadToolbar($locator)

        initInspector($locator)
        timerMan.register('end', 'creatingToolbar')
      }
    }
  }
}

export const toolbarMutations = {}
export const toolbarGetters = {
  showToolbarCards: (state, getters) =>
    getters.hasCardMethodAvailable || !!state?.dna?.cards,
  toolbarCardBrands: (state, getters) => {
    if (!state.dna) return

    const brandList = [...getters.cardBrandsWithoutCardBrandsAsMethod]

    // Add OSB brand if OSB brands are in DNA
    const osbBrands = [
      'PRV_OPT',
      'PRV_BDT',
      'PRV_BDP',
      'PRV_SOC',
      'PRV_SMART_CARD'
    ]
    for (const osbBrand of osbBrands) {
      if (brandList.includes(osbBrand)) {
        brandList.push('OSB')
        break
      }
    }

    const allowedBrandKeys = getters.isAnySimulatorOpen
      ? getAllowedBrandsFromNetworks({
          dnaBrands: brandList,
          paymentMethod: getters.paymentMethod,
          dnaMetadataCards:
            state.dna?.smartForm?.[getters.paymentMethod]?.metadata?.cards,
          disableVisaOnWallet: state.dna?.disableVisaOnWallet,
          CBEnabled:
            state.dna?.smartForm?.[getters.paymentMethod]?.metadata?.CBEnabled
        })
      : brandList.concat(state.dnaTestCards)

    const brands = DebugBarTestCards.brands
    const order = intersection(DebugBarTestCards.order, allowedBrandKeys)
    let filteredBrands = {}
    let brandKeys, brandObjects, testCards

    // Filter test card brands to just keep allowed one
    for (const key of allowedBrandKeys) {
      if (brands[key] && !brands[key]['_options'].hidden)
        filteredBrands[key] = brands[key]
    }

    // Pre-order brands with the default order
    brandKeys = order.slice(0)
    for (const key of Object.keys(filteredBrands)) {
      if (order.indexOf(key) < 0) brandKeys.push(key)
    }

    /**
     * Order the cards with the configured order received on the
     * testCardsResponse object (order)
     */
    brandKeys = sortBy(brandKeys, key => {
      if (isArray(order) && contains(order, key)) {
        return indexOf(order, key)
      }
      return order.length
    })

    return brandKeys
  },
  shouldSelectCardsMethod: (_, getters) =>
    getters.isSinglePaymentButton &&
    getters.hasCardMethodAvailable &&
    !getters.isActiveMethod('CARDS')
}
